import { createFileRoute, Link } from "@tanstack/react-router";
import { z } from "zod";
import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Mail, Copy, Check, Ticket } from "lucide-react";

export const Route = createFileRoute("/account-suspended")({
  head: () => ({
    meta: [
      { title: "Account Suspended — The Hospitality Reserve" },
      { name: "description", content: "This account has been suspended. Contact support if you believe this is a mistake." },
      { name: "robots", content: "noindex, nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://thehospitalityreserve.com/account-suspended" }],
  }),
  validateSearch: z.object({
    reason: z.string().max(240).optional(),
  }),
  component: AccountSuspendedPage,
});

function generateReference() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let result = "";
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

function AccountSuspendedPage() {
  const { reason } = Route.useSearch();
  const [contacted, setContacted] = useState(false);
  const [copied, setCopied] = useState(false);
  const [reference] = useState(() => generateReference());

  const displayReason = reason || "Your account has been suspended by an administrator.";
  const supportReference = `SUS-${reference}`;
  const copyableReference = `Reference: ${supportReference}\nReason: ${displayReason}`;

  const supportSubject = "Account suspension — appeal / enquiry";
  const supportBody = [
    "Hello The Hospitality Reserve support team,",
    "",
    "I am writing regarding the suspension of my account.",
    "",
    "Reference:",
    supportReference,
    "",
    "Reason provided on the suspension screen:",
    `"${displayReason}"`,
    "",
    "Account email: <please confirm the email on the account>",
    "",
    "I would like to:",
    "  • Understand the basis for this decision, and",
    "  • Request a review / appeal if appropriate.",
    "",
    "Thank you,",
  ].join("\n");
  const mailto =
    `mailto:Admin@Thehospitalityreserve.com` +
    `?subject=${encodeURIComponent(supportSubject)}` +
    `&body=${encodeURIComponent(supportBody)}`;

  const statusSubject = `Re: Account suspension — status check for reference ${supportReference}`;
  const statusBody = [
    "Hello The Hospitality Reserve support team,",
    "",
    `I previously submitted an appeal regarding my account suspension (Reference: ${supportReference}).`,
    "",
    "Could you confirm whether my request was received and let me know if there are any updates on my case?",
    "",
    "Thank you,",
  ].join("\n");
  const statusMailto =
    `mailto:Admin@Thehospitalityreserve.com` +
    `?subject=${encodeURIComponent(statusSubject)}` +
    `&body=${encodeURIComponent(statusBody)}`;

  const handleContactClick = useCallback(() => {
    setContacted(true);
  }, []);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(copyableReference);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: silently ignore if clipboard is unavailable.
    }
  }, [copyableReference]);

  return (
    <div className="min-h-dvh bg-background text-foreground flex flex-col">
      <header className="px-8 py-6 lg:px-16">
        <Link to="/" className="inline-block">
          <p className="tracking-wide-luxury text-accent">The Hospitality</p>
          <p className="font-display text-lg">Reserve</p>
        </Link>
      </header>

      <main className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-md text-center">
          <p className="tracking-wide-luxury text-destructive">Account suspended</p>
          <span className="gold-divider mt-6" />
          <h1 className="mt-6 font-display text-3xl">Access has been revoked.</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {displayReason}
          </p>
          <p className="mt-4 text-sm text-muted-foreground">
            You have been signed out. If you believe this is a mistake, contact our support team and we'll review your account.
          </p>

          <div className="mt-8 flex flex-col gap-3">
            {!contacted ? (
              <>
                <a
                  href={mailto}
                  onClick={handleContactClick}
                  className="inline-flex items-center justify-center w-full rounded-none bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury py-6 text-sm font-medium"
                  aria-label="Contact support about your account suspension (opens your email client with the suspension reason pre-filled)"
                >
                  <Mail className="mr-2 h-4 w-4" />
                  Contact support
                </a>
                <p className="text-xs text-muted-foreground">
                  Opens your email client with the suspension reason pre-filled. Or email{" "}
                  <a href={mailto} className="underline">Admin@Thehospitalityreserve.com</a> directly.
                </p>
              </>
            ) : (
              <div className="rounded-none border border-border bg-muted/40 p-5 text-left">
                <p className="text-sm font-medium text-foreground">Support request opened</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  If your email client did not open, you can also email{" "}
                  <a href={mailto} className="underline">Admin@Thehospitalityreserve.com</a>.
                </p>
                <div className="mt-4">
                  <p className="text-xs tracking-wide-luxury text-muted-foreground">Copyable reference</p>
                  <div className="mt-2 flex items-center gap-2">
                    <code className="flex-1 truncate rounded-sm bg-background px-3 py-2 text-xs text-foreground">
                      {supportReference}
                    </code>
                    <button
                      type="button"
                      onClick={handleCopy}
                      className="inline-flex items-center justify-center rounded-sm border border-border bg-background px-3 py-2 text-xs font-medium hover:bg-muted transition-colors"
                      aria-label="Copy support reference and reason to clipboard"
                    >
                      {copied ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
                      <span className="ml-2">{copied ? "Copied" : "Copy"}</span>
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">Reason: {displayReason}</p>

                  <div className="mt-5">
                    <p className="text-xs tracking-wide-luxury text-muted-foreground">Ticket status</p>
                    <a
                      href={statusMailto}
                      className="mt-2 inline-flex items-center justify-center w-full rounded-sm border border-border bg-background px-3 py-3 text-xs font-medium hover:bg-muted transition-colors"
                      aria-label="Send a status-check email to confirm your request has been received"
                    >
                      <Ticket className="mr-2 h-4 w-4" />
                      Check ticket status
                    </a>
                    <p className="mt-2 text-xs text-muted-foreground">
                      Opens a status-check email to confirm your request has been received.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <Button
              variant="outline"
              asChild
              className="w-full rounded-none tracking-wide-luxury py-6"
            >
              <Link to="/auth">Return to sign in</Link>
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}

